import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import PriceItem from './PriceItem';
import ClearButton from '../../../shared/components/Prices/ClearButton';
import CurrencyIcon from '../../../shared/components/Icons/Currency';
import Scrollable from '../../../shared/components/Scrollable';
import usePrices from '../../../hooks/usePrices';
import Tooltip from '../Tooltip';
import { isMobile } from '../../../helpers/device';
import { getBoundingClientRect } from '../../../helpers/coordinates';

interface PricesProps {
  isFullscreen?: boolean;
}

const Prices: React.FC<PricesProps> = ({ isFullscreen }) => {
  const { pricesPreview, isPeriodical, withoutRendering } = usePrices();
  const [tooltipData, setTooltipData] = useState<{ left: string; content: string } | null>(null);

  const containerRef = useRef<HTMLDivElement>();

  const showTooltip = (tooltipData) => {
    let closeTimer;

    if (containerRef.current) {
      tooltipData.left -= getBoundingClientRect(containerRef.current).left;
      setTooltipData(tooltipData);

      if (closeTimer) {
        clearTimeout(closeTimer);
      }
    }

    if (isMobile()) {
      closeTimer = setTimeout(hideTooltip, 2000);
    }
  };

  const hideTooltip = () => setTooltipData(null);

  if (withoutRendering) return null;

  return (
    <Container
      onMouseMove={(e) => {
        e.stopPropagation();
      }}
      isFullscreen={isFullscreen}
      isPeriodical={isPeriodical}
      ref={containerRef}
    >
      {tooltipData && (
        <Tooltip
          style={{ left: tooltipData.left }}
          message={[`${tooltipData.content} `, <CurrencyIcon fill="#fff" width={8} height={10} />]}
        />
      )}
      <Scrollable>
        <ListWrapper>
          <PriceSign>
            <CurrencyIcon opacity={0.4} />
          </PriceSign>
          <List>
            {pricesPreview.map((price) => (
              <PriceItem
                key={price.value}
                price={price}
                mouseEnterHandler={showTooltip}
                mouseLeaveHandler={hideTooltip}
              />
            ))}
          </List>
          <ClearButton />
        </ListWrapper>
      </Scrollable>
    </Container>
  );
};

const Container = styled.div<any>`
  max-width: 100%;
  width: 100%;
  padding-top: 17px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin: auto;
  text-align: center;

  ${(props) =>
    props.isPeriodical &&
    css`
      top: 42px;

      @media screen and (max-width: 700px) {
        top: 100px;
      }
    `}

  @media screen and (max-width: 700px) {
    padding-top: 8px;
  }
`;

const ListWrapper = styled.div<any>`
  display: flex;
  align-items: center;
  padding: 0 16px;
  margin: 0 auto;
  z-index: 10;

  border-top-left-radius: 0;
  border-top-right-radius: 0;
`;

const List = styled.ul<any>`
  display: flex;
  vertical-align: middle;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const PriceSign = styled.div<any>`
  display: inline-block;
  vertical-align: middle;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.4);
`;

export default Prices;
